// ──────────────────────────────────────────────
// RecordingService — continuous MediaRecorder with init-segment prepending
//
// Replaces the per-chunk RecordRTC approach with a single continuous
// MediaRecorder using native `timeslice`. Every chunk is made independently
// decodable by prepending the init segment extracted from chunk 0.
//
// See ADR-006 for design rationale.
//
// Usage:
//   const recording = new RecordingService(config);
//   await recording.start(stream, sessionId);
//   // ... chunks are persisted + enqueued automatically
//   await recording.stop();
//   recording.dispose();
// ──────────────────────────────────────────────

import SentryService from '@services/SentryService';

import { AdaptiveMicLevelMeter, rmsFromTimeDomain } from '../audio/AdaptiveMicLevelMeter';
import type { HealthState } from '../audio/types';
import type { StorageService } from '../storage';
import type { UploadService } from '../upload';
import { BaseService } from '../upload/BaseService';

import { BackgroundThrottleGuard } from './BackgroundThrottleGuard';
import { analyzeChunkSilence, type ChunkSilenceResult } from './ChunkSilenceAnalyzer';
import { extractInitSegment, prependInitSegment } from './InitSegmentExtractor';
import type {
  RecordingEventMap,
  RecordingServiceConfig,
  RecordingServiceStatus,
  RecordingStartOptions,
  RecordingState,
} from './types';
import {
  DEFAULT_MAX_CHUNK_BYTES,
  DEFAULT_REQUEST_DATA_INTERVAL_MS,
  DEFAULT_STAGING_WRITE_INTERVAL_MS,
  DEFAULT_TIMESLICE_MS,
  getSupportedMimeType,
  INIT_SEGMENT_CHUNK_INDEX,
  RECORDING_LOCK_NAME,
  STAGING_CHUNK_INDEX,
} from './types';

// ── Helpers ──

function initialRecordingState(): RecordingState {
  return {
    status: 'idle',
    duration: 0,
    chunkCount: 0,
    activeDeviceId: null,
    mimeType: null,
    initSegmentCaptured: false,
    error: null,
    mixerSuspended: false,
    micMuted: false,
  };
}

let chunkIdCounter = 0;
function nextChunkId(): string {
  return `chunk-${Date.now()}-${++chunkIdCounter}`;
}

/** Audio level polling interval — matches WebRecordingService's 50ms cadence */
const AUDIO_LEVEL_POLL_MS = 50;
/** AnalyserNode FFT size — matches AudioHealthMonitor */
const ANALYSER_FFT_SIZE = 1024; // time-domain RMS window (~21ms @48k) for the honest level meter

/**
 * RecordingService — owns the MediaRecorder lifecycle, init-segment
 * extraction/prepending, crash-recovery writes, and chunk finalization.
 *
 * Consumes StorageService for persistence and UploadService for upload.
 * Framework-agnostic: React connects via useSyncExternalStore.
 */
export class RecordingService extends BaseService<RecordingState, RecordingEventMap> {
  private readonly storageService: StorageService;
  private readonly uploadService: UploadService;
  private readonly timesliceMs: number;
  private readonly requestDataIntervalMs: number;
  private readonly maxChunkBytes: number;
  private readonly stagingWriteIntervalMs: number;
  private readonly getHealthState: (() => HealthState | null) | null;

  // Pipeline serializer — all mutations to pendingChunks and storageService writes
  // go through this queue to prevent overlapping OPFS writes.
  private pipelineQueue: Promise<void> = Promise.resolve();

  // Staging write throttle — reduces OPFS write frequency from every 2s to every 10s
  private stagingWritePending = false;
  private stagingWriteTimerId: ReturnType<typeof setTimeout> | null = null;

  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private sessionId: string | null = null;

  // Init-segment state
  private initSegment: ArrayBuffer | null = null;
  private mimeType: string | null = null;
  private isFirstChunk = true;

  // Chunk accumulation between upload boundaries
  private pendingChunks: ArrayBuffer[] = [];
  private pendingBytesSize = 0;

  // Timers
  private requestDataTimerId: ReturnType<typeof setInterval> | null = null;
  private uploadBoundaryTimerId: ReturnType<typeof setInterval> | null = null;
  private durationTimerId: ReturnType<typeof setInterval> | null = null;
  private recordingStartTime = 0;
  private pausedDuration = 0;
  private pauseStartTime = 0;

  // Chunk index counter (0-based, incremented on each finalized upload)
  private nextChunkIndex = 0;

  // Web Lock
  private lockRelease: (() => void) | null = null;

  // Wake Lock
  private wakeLock: WakeLockSentinel | null = null;

  // Lifecycle
  private disposed = false;

  // Audio level analysis
  private levelAudioContext: AudioContext | null = null;
  private levelAnalyser: AnalyserNode | null = null;
  private levelSourceNode: MediaStreamAudioSourceNode | null = null;
  private audioLevelTimerId: ReturnType<typeof setInterval> | null = null;
  private levelTimeData: Float32Array<ArrayBuffer> | null = null;
  private readonly levelMeter = new AdaptiveMicLevelMeter();

  // Background throttle guard (RTCPeerConnection loopback)
  private throttleGuard = new BackgroundThrottleGuard();
  private audioLevelWorker: Worker | null = null;
  private lastRequestDataTimestamp = 0;

  // Mixer integration (Phase 3b)
  private mixerAudioContext: AudioContext | null = null;
  private mixerStateInterval: ReturnType<typeof setInterval> | null = null;
  private mixerSuspended = false;
  private rawMicStream: MediaStream | null = null;

  // Visibility handler ref for cleanup
  private readonly handleVisibilityChange: () => void;

  // Tab lifecycle handler refs for cleanup
  private readonly handleFreeze: () => void;
  private readonly handleResume: () => void;

  constructor(config: RecordingServiceConfig) {
    super(initialRecordingState());

    this.storageService = config.storageService;
    this.uploadService = config.uploadService;
    this.timesliceMs = config.timesliceMs ?? DEFAULT_TIMESLICE_MS;
    this.requestDataIntervalMs = config.requestDataIntervalMs ?? DEFAULT_REQUEST_DATA_INTERVAL_MS;
    this.maxChunkBytes = config.maxChunkBytes ?? DEFAULT_MAX_CHUNK_BYTES;
    this.stagingWriteIntervalMs = config.stagingWriteIntervalMs ?? DEFAULT_STAGING_WRITE_INTERVAL_MS;
    this.getHealthState = config.getHealthState ?? null;

    this.handleVisibilityChange = this.onVisibilityChange.bind(this);
    this.handleFreeze = this.onFreeze.bind(this);
    this.handleResume = this.onResume.bind(this);
  }

  // ── Public API ──

  /**
   * Start recording from the given MediaStream.
   *
   * Acquires Web Lock (cross-tab exclusion), Wake Lock (prevent sleep),
   * creates MediaRecorder with timeslice, and starts the dual-interval pipeline.
   */
  async start(stream: MediaStream, sessionId: string, options?: RecordingStartOptions): Promise<void> {
    if (this.disposed) throw new Error('RecordingService is disposed');
    if (this.state.status !== 'idle' && this.state.status !== 'error') {
      throw new Error(`Cannot start recording in state: ${this.state.status}`);
    }

    // Release any stale lock from a previous failed recording.
    this.releaseWebLock();

    this.stream = stream;
    this.sessionId = sessionId;
    this.isFirstChunk = true;
    this.initSegment = null;
    this.pendingChunks = [];
    this.pendingBytesSize = 0;
    this.nextChunkIndex = 0;
    this.pausedDuration = 0;
    this.pauseStartTime = 0;
    this.pipelineQueue = Promise.resolve();
    this.cancelPendingStagingWrite();

    // Detect MIME type
    this.mimeType = getSupportedMimeType() ?? null;
    if (!this.mimeType) {
      this.handleError('No supported audio MIME type found for MediaRecorder');
      return;
    }

    // Acquire Web Lock for cross-tab exclusion
    const lockAcquired = await this.acquireWebLock();
    if (!lockAcquired) {
      this.handleError('Another tab is already recording (Web Lock held)');
      return;
    }

    // Acquire Wake Lock
    await this.acquireWakeLock();

    try {
      // Create and start MediaRecorder
      this.recorder = new MediaRecorder(stream, { mimeType: this.mimeType });
      this.setupRecorderHandlers();

      // Use explicit deviceId from options (mixer destination tracks return undefined),
      // fall back to stream track settings for non-mixer recordings
      const deviceId = options?.deviceId ?? stream.getAudioTracks()[0]?.getSettings()?.deviceId ?? null;

      // Store mixer references
      this.mixerAudioContext = options?.mixerAudioContext ?? null;
      this.rawMicStream = options?.rawMicStream ?? null;

      // Start recording with timeslice — produces ondataavailable every timesliceMs
      this.recorder.start(this.timesliceMs);

      // Start timers
      this.recordingStartTime = performance.now();
      this.startDurationTimer();
      this.startRequestDataTimer();
      this.startUploadBoundaryTimer();
      this.startAudioLevelAnalysis(stream);

      // Listen for visibility changes (flush on hidden, re-acquire wake lock on visible)
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      // Chrome Page Lifecycle API — observe freeze/resume for diagnostics
      document.addEventListener('freeze', this.handleFreeze);
      document.addEventListener('resume', this.handleResume);

      // Activate RTC loopback to prevent Chrome background tab throttling.
      // Raw mic stream used for guard — AudioContext destination tracks may not prevent Chrome throttling.
      void this.throttleGuard.activate(options?.rawMicStream ?? stream);

      // Start mixer state monitoring if mixer AudioContext is provided
      this.startMixerStateMonitoring();

      // Update state
      this.setServiceStatus('recording');
      this.updateState({
        activeDeviceId: deviceId,
        mimeType: this.mimeType,
        error: null,
        micMuted: false, // fresh streams — any prior chaos mute is gone
      });

      SentryService.addBreadcrumb({
        category: 'recording.service',
        message: `Recording started (mimeType=${this.mimeType}, timeslice=${this.timesliceMs}ms)`,
        level: 'info',
        data: { sessionId, deviceId },
      });
    } catch (err) {
      this.releaseWebLock();
      this.releaseWakeLock();
      this.handleError(
        `Failed to start MediaRecorder: ${err instanceof Error ? err.message : String(err)}`,
        err,
      );
    }
  }

  /** Stop recording and finalize any remaining chunks. */
  async stop(): Promise<void> {
    if (this.state.status !== 'recording' && this.state.status !== 'paused') {
      // Defense-in-depth: release locks even from non-stoppable states (e.g. 'error')
      this.releaseWebLock();
      this.releaseWakeLock();
      return;
    }

    this.setServiceStatus('stopping');

    // Stop timers first
    this.stopTimers();

    // Stop MediaRecorder — triggers final ondataavailable
    const recorder = this.recorder;
    if (recorder && recorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        const onStop = () => {
          recorder.removeEventListener('stop', onStop);
          resolve();
        };
        recorder.addEventListener('stop', onStop);
        recorder.stop();
      });
    }

    // Cancel any pending throttled staging write — we're about to finalize everything
    this.cancelPendingStagingWrite();

    // Restore mic tracks if a chaos mute is active — the raw mic stream outlives
    // this recording (AudioInputDeviceService reuses it) and must not stay dead.
    if (this.state.micMuted) {
      this.setMicMuted(false);
    }

    // Finalize any remaining accumulated chunks (serialized through pipeline queue)
    await this.enqueuePipelineOp(() => this.finalizeAndUpload());

    // Cleanup
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('freeze', this.handleFreeze);
    document.removeEventListener('resume', this.handleResume);
    this.throttleGuard.deactivate();
    this.releaseWakeLock();
    this.releaseWebLock();
    this.recorder = null;

    // Cleanup mixer references
    if (this.mixerStateInterval) {
      clearInterval(this.mixerStateInterval);
      this.mixerStateInterval = null;
    }
    this.mixerAudioContext = null;
    this.mixerSuspended = false;
    this.rawMicStream = null;

    this.setServiceStatus('idle');

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: `Recording stopped (chunks=${this.state.chunkCount})`,
      level: 'info',
      data: { sessionId: this.sessionId },
    });
  }

  /** Pause the MediaRecorder. */
  pause(): void {
    if (this.state.status !== 'recording') return;

    if (this.recorder && this.recorder.state === 'recording') {
      this.recorder.pause();
    }

    this.pauseStartTime = performance.now();
    this.stopTimers();
    this.releaseWakeLock();
    this.setServiceStatus('paused');

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: 'Recording paused',
      level: 'info',
    });
  }

  /**
   * Resume from a cold start (e.g., tab reload while recording was paused).
   * Creates a new MediaRecorder without resetting chunk state.
   * Enters 'paused' state — caller must call resume() to begin recording.
   */
  async resumeFromColdStart(
    stream: MediaStream,
    sessionId: string,
    nextChunkIndex: number,
  ): Promise<void> {
    if (this.recorder) {
      throw new Error('Cannot cold-resume: recorder already exists');
    }

    this.stream = stream;
    this.sessionId = sessionId;
    this.nextChunkIndex = nextChunkIndex;
    this.pendingChunks = [];
    this.pendingBytesSize = 0;

    // Restore init segment from storage
    const stored = await this.storageService.readChunk(sessionId, INIT_SEGMENT_CHUNK_INDEX);
    if (stored) {
      this.initSegment = stored;
      this.isFirstChunk = false;
    } else {
      this.isFirstChunk = true;
      this.initSegment = null;
    }

    // Determine MIME type
    this.mimeType = getSupportedMimeType() ?? null;
    if (!this.mimeType) {
      throw new Error('No supported MIME type for MediaRecorder');
    }

    // Create recorder (inactive — not started yet)
    this.recorder = new MediaRecorder(stream, { mimeType: this.mimeType });
    this.setupRecorderHandlers();

    // Acquire Web Lock for cross-tab exclusion
    const lockAcquired = await this.acquireWebLock();
    if (!lockAcquired) {
      this.recorder = null;
      throw new Error('Another tab is already recording (Web Lock held)');
    }

    // Set paused state — user will click resume to start
    this.setServiceStatus('paused');
    this.updateState({
      activeDeviceId: stream.getAudioTracks()[0]?.getSettings().deviceId ?? null,
      mimeType: this.mimeType,
      initSegmentCaptured: this.initSegment !== null,
    });

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: `Cold-resumed session ${sessionId} at chunk ${nextChunkIndex}`,
      level: 'info',
    });
  }

  /** Resume the MediaRecorder from paused state. */
  async resume(): Promise<void> {
    if (this.state.status !== 'paused') return;

    if (this.pauseStartTime > 0) {
      this.pausedDuration += performance.now() - this.pauseStartTime;
      this.pauseStartTime = 0;
    }

    // Resume or start MediaRecorder
    if (this.recorder) {
      if (this.recorder.state === 'inactive') {
        // Cold-resumed: recorder was created but never started
        this.recorder.start(this.timesliceMs);
      } else if (this.recorder.state === 'paused') {
        this.recorder.resume();
      }
    }

    this.startDurationTimer();
    this.startRequestDataTimer();
    this.startUploadBoundaryTimer();
    if (this.stream) {
      this.startAudioLevelAnalysis(this.stream);
    }
    await this.acquireWakeLock();

    // Register visibility listener (may be missing after cold-resume)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.setServiceStatus('recording');

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: 'Recording resumed',
      level: 'info',
    });
  }

  /** Update the active device ID in state (e.g. after mid-recording device switch). */
  updateDeviceId(deviceId: string): void {
    this.updateState({ activeDeviceId: deviceId });
    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: `Device ID updated: ${deviceId}`,
      level: 'info',
    });
  }

  /** Re-activate BackgroundThrottleGuard with a new raw mic stream after mid-recording device switch. */
  updateRawMicStream(rawMicStream: MediaStream): void {
    this.rawMicStream = rawMicStream;
    this.throttleGuard.deactivate();
    void this.throttleGuard.activate(rawMicStream);

    // Keep the chaos mute in effect across a device switch
    if (this.state.micMuted) {
      this.applyMicEnabled(false);
    }
  }

  /**
   * Mute/unmute the mic input by disabling its audio tracks (dev/chaos control).
   * A disabled track produces bit-exact zero samples — the dead-mic signature
   * GDN-2204 detects — so this deterministically triggers silent chunks and
   * drives AudioHealthMonitor to 'failed' while recording continues.
   */
  setMicMuted(muted: boolean): void {
    if (this.state.micMuted === muted) return;

    this.applyMicEnabled(!muted);
    this.updateState({ micMuted: muted });

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: `Mic ${muted ? 'muted' : 'unmuted'} via chaos control`,
      level: 'warning',
      data: { sessionId: this.sessionId },
    });
  }

  /** Set enabled on every audio track we hold (recorded stream + raw mic). */
  private applyMicEnabled(enabled: boolean): void {
    for (const stream of [this.stream, this.rawMicStream]) {
      stream?.getAudioTracks().forEach((track) => {
        track.enabled = enabled;
      });
    }
  }

  /** Flush the current MediaRecorder chunk (e.g. before device swap for a clean boundary). */
  flush(): void {
    if (this.recorder && this.recorder.state === 'recording') {
      try {
        this.recorder.requestData();
      } catch {
        // requestData can throw if recorder is in wrong state
      }
    }
  }

  override dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    this.stopTimers();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('freeze', this.handleFreeze);
    document.removeEventListener('resume', this.handleResume);
    this.throttleGuard.deactivate();

    if (this.recorder && this.recorder.state !== 'inactive') {
      try {
        this.recorder.stop();
      } catch {
        // Ignore — recorder may already be stopped
      }
    }
    this.recorder = null;

    this.releaseWakeLock();
    this.releaseWebLock();

    if (this.state.micMuted) {
      this.applyMicEnabled(true);
    }

    this.pendingChunks = [];
    this.initSegment = null;
    this.stream = null;
    this.sessionId = null;

    this.setState(initialRecordingState());

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: 'RecordingService disposed',
      level: 'info',
    });

    super.dispose();
  }

  // ── MediaRecorder Event Handlers ──

  private setupRecorderHandlers(): void {
    if (!this.recorder) return;

    this.recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data.size === 0) return; // Safari can produce zero-byte chunks

      // Serialize blob conversion + mutation + storage write through the pipeline.
      // Keeps blob→ArrayBuffer inside the queue so conversion errors are caught
      // by processChunkData's error handler.
      const blob = event.data;
      void this.enqueuePipelineOp(async () => {
        const arrayBuffer = await blob.arrayBuffer();
        await this.processChunkData(arrayBuffer);
      });
    };

    this.recorder.onerror = () => {
      this.handleRecorderError();
    };
  }

  /**
   * Process a single chunk's ArrayBuffer — called inside the pipeline queue.
   * Handles both first-chunk (init segment extraction) and subsequent chunks.
   */
  private async processChunkData(arrayBuffer: ArrayBuffer): Promise<void> {
    try {
      if (this.isFirstChunk) {
        // Chunk 0: extract init segment
        this.isFirstChunk = false;
        await this.processFirstChunk(arrayBuffer);
      } else {
        // Subsequent chunk: accumulate for the upload boundary
        this.pendingChunks.push(arrayBuffer);
        this.pendingBytesSize += arrayBuffer.byteLength;

        // Schedule a throttled staging write for crash recovery
        this.scheduleStagingWrite();
      }
    } catch (err) {
      const message = `Error processing chunk: ${err instanceof Error ? err.message : String(err)}`;
      SentryService.captureException(err instanceof Error ? err : new Error(message));
      this.emit('chunkError', { chunkId: 'unknown', error: message });
    }
  }

  private async processFirstChunk(data: ArrayBuffer): Promise<void> {
    if (!this.mimeType || !this.sessionId) return;

    // Extract init segment
    this.initSegment = await extractInitSegment(data, this.mimeType);

    // Push chunk 0 to pending BEFORE storage write so it's not lost on failure
    this.pendingChunks.push(data);
    this.pendingBytesSize += data.byteLength;

    // Persist init segment to StorageService for crash recovery (best-effort —
    // init segment is in memory, crash recovery is degraded but recording proceeds)
    try {
      await this.storageService.writeChunk(this.sessionId, INIT_SEGMENT_CHUNK_INDEX, this.initSegment);
    } catch (err) {
      SentryService.captureException(
        err instanceof Error ? err : new Error(`Init segment write failed: ${String(err)}`),
      );
    }

    this.updateState({ initSegmentCaptured: true });
    this.emit('initSegmentCaptured', {
      mimeType: this.mimeType,
      byteSize: this.initSegment.byteLength,
    });

    SentryService.addBreadcrumb({
      category: 'recording.service',
      message: `Init segment captured (${this.initSegment.byteLength} bytes)`,
      level: 'info',
    });

    // Schedule a throttled staging write for crash recovery
    this.scheduleStagingWrite();
  }

  private handleRecorderError(): void {
    // GDN-2205: a real Sentry event, not just a breadcrumb — a recovered
    // recorder error was previously invisible unless the recording died,
    // which hid the "recorder failing while audio-health is fine" cases.
    SentryService.logEvent('MediaRecorder error — attempting recovery', {
      level: 'warning',
      extra: {
        sessionId: this.sessionId,
        streamActive: this.stream?.active ?? false,
        healthState: this.getHealthState?.() ?? null,
        chunkCount: this.state.chunkCount,
        duration: this.state.duration,
      },
    });

    // MediaRecorder transitions to inactive on error.
    // Recovery: create new MediaRecorder against the same stream.
    if (this.stream && this.stream.active && this.sessionId) {
      try {
        const mimeType = this.mimeType ?? getSupportedMimeType();
        if (!mimeType) {
          this.handleError('Cannot recover MediaRecorder — no supported MIME type');
          return;
        }

        this.recorder = new MediaRecorder(this.stream, { mimeType });
        this.setupRecorderHandlers();

        // Fresh init segment needed from the new recorder
        this.isFirstChunk = true;
        this.initSegment = null;
        this.updateState({ initSegmentCaptured: false });

        this.recorder.start(this.timesliceMs);

        SentryService.addBreadcrumb({
          category: 'recording.service',
          message: 'MediaRecorder recovered successfully',
          level: 'info',
        });
      } catch (err) {
        this.handleError(
          `MediaRecorder recovery failed: ${err instanceof Error ? err.message : String(err)}`,
          err,
        );
      }
    } else {
      this.handleError('Cannot recover MediaRecorder — stream is no longer active');
    }
  }

  // ── Chunk Pipeline ──

  /**
   * Serialize an async operation through the pipeline queue.
   * Guarantees that mutations to pendingChunks and storageService writes
   * never overlap, preventing OPFS createSyncAccessHandle exclusivity failures.
   */
  private enqueuePipelineOp(fn: () => Promise<void>): Promise<void> {
    const op = this.pipelineQueue.then(fn, fn);
    this.pipelineQueue = op.catch(() => {}); // swallow to keep chain alive
    return op;
  }

  /**
   * Schedule a throttled staging write. Only one write per stagingWriteIntervalMs.
   * Reduces OPFS write frequency from every ~2s to every ~10s.
   */
  private scheduleStagingWrite(): void {
    if (this.stagingWritePending || this.stagingWriteTimerId !== null) return;
    this.stagingWritePending = true;
    this.stagingWriteTimerId = setTimeout(() => {
      this.stagingWriteTimerId = null;
      this.stagingWritePending = false;
      void this.enqueuePipelineOp(() => this.writeStagingChunk());
    }, this.stagingWriteIntervalMs);
  }

  /** Cancel any pending throttled staging write. */
  private cancelPendingStagingWrite(): void {
    if (this.stagingWriteTimerId !== null) {
      clearTimeout(this.stagingWriteTimerId);
      this.stagingWriteTimerId = null;
    }
    this.stagingWritePending = false;
  }

  /**
   * Write all accumulated chunk data to the staging key for crash recovery.
   * Concatenates pendingChunks so a crash loses at most ~stagingWriteIntervalMs of audio.
   */
  private async writeStagingChunk(): Promise<void> {
    if (!this.sessionId || this.pendingChunks.length === 0) return;
    try {
      let data: ArrayBuffer;
      if (this.pendingChunks.length === 1) {
        data = this.pendingChunks[0];
      } else {
        const totalSize = this.pendingChunks.reduce((sum, c) => sum + c.byteLength, 0);
        const merged = new Uint8Array(totalSize);
        let offset = 0;
        for (const chunk of this.pendingChunks) {
          merged.set(new Uint8Array(chunk), offset);
          offset += chunk.byteLength;
        }
        data = merged.buffer;
      }
      await this.storageService.writeChunk(this.sessionId, STAGING_CHUNK_INDEX, data);
    } catch {
      // Staging writes are best-effort — don't fail the recording
    }
  }

  /**
   * Finalize accumulated chunks: concatenate, prepend init segment,
   * persist to StorageService, and enqueue to UploadService.
   */
  private async finalizeAndUpload(): Promise<void> {
    if (!this.sessionId || this.pendingChunks.length === 0) return;

    const chunkIndex = this.nextChunkIndex++;
    const chunkId = nextChunkId();
    const contentType = this.mimeType ?? 'audio/webm';

    // GDN-2205: the MediaRecorder → finalize-chunk step had the least telemetry
    // in the pipeline. The span carries silence/health per chunk so "health fine
    // but recorder writes empty" is separable in Sentry (isSilent=true + healthy).
    const span = SentryService.startInactiveSpan(
      'recording.chunk.finalize',
      `Finalize chunk ${chunkIndex}`,
    );
    span?.setAttribute('chunkIndex', chunkIndex);
    span?.setAttribute('pendingParts', this.pendingChunks.length);

    try {
      // Concatenate pending chunks
      let data: ArrayBuffer;
      if (this.pendingChunks.length === 1) {
        data = this.pendingChunks[0];
      } else {
        const totalSize = this.pendingChunks.reduce((sum, c) => sum + c.byteLength, 0);
        const merged = new Uint8Array(totalSize);
        let offset = 0;
        for (const chunk of this.pendingChunks) {
          merged.set(new Uint8Array(chunk), offset);
          offset += chunk.byteLength;
        }
        data = merged.buffer;
      }

      // Prepend init segment for chunks after chunk 0
      if (chunkIndex > 0 && this.initSegment) {
        data = prependInitSegment(this.initSegment, data);
      }

      const byteSize = data.byteLength;

      // Zero-signal detection + health snapshot, read at chunk-finalize time
      // and before enqueue so the metadata can travel with the upload (GDN-2204).
      // Both are best-effort — analysis must never fail or delay-block the chunk.
      let silence: ChunkSilenceResult | null = null;
      const analysisStart = performance.now();
      try {
        silence = await analyzeChunkSilence(data);
      } catch {
        // Treat as "unknown" — never as silent
      }
      let healthState: HealthState | null = null;
      try {
        healthState = this.getHealthState?.() ?? null;
      } catch {
        // Health provider errors must not fail the chunk
      }

      span?.setAttribute('byteSize', byteSize);
      span?.setAttribute('silenceAnalysisMs', Math.round(performance.now() - analysisStart));
      span?.setAttribute('isSilent', silence?.isSilent ?? 'unknown');
      if (silence) span?.setAttribute('peakLevel', silence.peakLevel);
      if (healthState) span?.setAttribute('healthState', healthState);

      // Persist to StorageService at permanent chunk index
      await this.storageService.writeChunk(this.sessionId, chunkIndex, data);

      // Clean up staging data
      try {
        await this.storageService.deleteChunk(this.sessionId, STAGING_CHUNK_INDEX);
      } catch {
        // Best-effort cleanup
      }

      // Enqueue to UploadService (fire-and-forget)
      this.uploadService.enqueue({
        sessionId: this.sessionId,
        chunkIndex,
        chunkId,
        contentType,
        byteSize,
      });

      // Update state
      this.updateState({ chunkCount: this.state.chunkCount + 1 });

      // Emit event for RecordingContext integration
      this.emit('chunkReady', {
        sessionId: this.sessionId,
        chunkIndex,
        chunkId,
        contentType,
        byteSize,
        isSilent: silence?.isSilent ?? null,
        peakLevel: silence?.peakLevel ?? null,
        healthState,
      });

      // Per-chunk signal: unlike AudioHealthMonitor's transition-only events,
      // every chunk carries health + silence, so a session that starts broken
      // and stays broken keeps emitting (GDN-2204 blind-spot fix).
      SentryService.addBreadcrumb({
        category: 'recording.service',
        message: `Chunk ${chunkIndex} finalized (${byteSize} bytes)`,
        level: silence?.isSilent ? 'warning' : 'info',
        data: {
          chunkId,
          sessionId: this.sessionId,
          isSilent: silence?.isSilent ?? null,
          peakLevel: silence?.peakLevel ?? null,
          healthState,
        },
      });

      // Reset accumulation only on success — keep chunks in memory for retry on failure
      this.pendingChunks = [];
      this.pendingBytesSize = 0;

      span?.setStatus({ code: 1 });
    } catch (err) {
      const message = `Failed to finalize chunk ${chunkIndex}: ${err instanceof Error ? err.message : String(err)}`;
      span?.setStatus({ code: 2 });
      span?.setAttribute('error', message);
      SentryService.captureException(err instanceof Error ? err : new Error(message));
      this.emit('chunkError', { chunkId, error: message });
    } finally {
      span?.end();
    }
  }

  // ── Timers ──

  private startDurationTimer(): void {
    this.stopDurationTimer();
    this.durationTimerId = setInterval(() => {
      const elapsed = performance.now() - this.recordingStartTime - this.pausedDuration;
      this.updateState({ duration: Math.floor(elapsed / 1000) });
    }, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationTimerId !== null) {
      clearInterval(this.durationTimerId);
      this.durationTimerId = null;
    }
  }

  /**
   * Start the 2s requestData() timer for crash recovery.
   * This forces MediaRecorder to fire ondataavailable, giving us
   * fresh data to persist. At most ~2s of audio lost on crash.
   */
  private startRequestDataTimer(): void {
    this.stopRequestDataTimer();
    this.requestDataTimerId = setInterval(() => {
      this.lastRequestDataTimestamp = Date.now();
      if (this.recorder && this.recorder.state === 'recording') {
        try {
          this.recorder.requestData();
        } catch {
          // requestData can throw if recorder is in wrong state
        }
      }
    }, this.requestDataIntervalMs);
  }

  private stopRequestDataTimer(): void {
    if (this.requestDataTimerId !== null) {
      clearInterval(this.requestDataTimerId);
      this.requestDataTimerId = null;
    }
  }

  /**
   * Start the 30s upload boundary timer.
   * On each tick, finalize accumulated chunks and enqueue to UploadService.
   */
  private startUploadBoundaryTimer(): void {
    this.stopUploadBoundaryTimer();
    this.uploadBoundaryTimerId = setInterval(() => {
      this.cancelPendingStagingWrite();
      void this.enqueuePipelineOp(() => this.finalizeAndUpload());
    }, this.timesliceMs);
  }

  private stopUploadBoundaryTimer(): void {
    if (this.uploadBoundaryTimerId !== null) {
      clearInterval(this.uploadBoundaryTimerId);
      this.uploadBoundaryTimerId = null;
    }
  }

  /** Poll mixer AudioContext state every 3s to detect suspend/resume transitions. */
  private startMixerStateMonitoring(): void {
    if (!this.mixerAudioContext) return;

    if (this.mixerStateInterval) {
      clearInterval(this.mixerStateInterval);
    }

    this.mixerStateInterval = setInterval(() => {
      if (!this.mixerAudioContext) return;

      const suspended = this.mixerAudioContext.state === 'suspended';
      if (suspended !== this.mixerSuspended) {
        this.mixerSuspended = suspended;
        this.updateState({ mixerSuspended: suspended });

        if (suspended) {
          SentryService.addBreadcrumb({
            category: 'recording.service',
            message: 'Mixer AudioContext suspended — recording silence',
            level: 'warning',
          });
        }
      }
    }, 3_000);
  }

  private stopTimers(): void {
    this.stopDurationTimer();
    this.stopRequestDataTimer();
    this.stopUploadBoundaryTimer();
    this.cancelPendingStagingWrite();
    this.stopAudioLevelAnalysis();
  }

  // ── Audio Level Analysis (feeds UI meter via audioLevel events) ──

  private startAudioLevelAnalysis(stream: MediaStream): void {
    // Stop any previous analysis (e.g. from a previous start before stop)
    this.stopAudioLevelAnalysis();

    try {
      this.levelAudioContext = new AudioContext();
      this.levelAnalyser = this.levelAudioContext.createAnalyser();
      this.levelAnalyser.fftSize = ANALYSER_FFT_SIZE;
      this.levelAnalyser.smoothingTimeConstant = 0; // we smooth the level ourselves
      this.levelTimeData = new Float32Array(this.levelAnalyser.fftSize);
      this.levelMeter.reset();

      this.levelSourceNode = this.levelAudioContext.createMediaStreamSource(stream);
      this.levelSourceNode.connect(this.levelAnalyser);

      // Use Web Worker for audio level polling — immune to background tab throttling.
      // Honest level: time-domain RMS -> dBFS -> per-mic adaptive noise floor (so the
      // waveform is flat in a quiet room and responsive to voice). See AdaptiveMicLevelMeter.
      const pollAudioLevel = () => {
        if (!this.levelAnalyser || !this.levelTimeData) return;
        if (document.hidden) return; // Skip audio analysis when tab isn't visible
        this.levelAnalyser.getFloatTimeDomainData(this.levelTimeData);
        const rms = rmsFromTimeDomain(this.levelTimeData);
        this.emit('audioLevel', { level: this.levelMeter.update(rms) * 100 });
      };

      try {
        this.audioLevelWorker = new Worker(
          new URL('./audio-level-timer.worker.ts', import.meta.url),
          { type: 'module' },
        );
        this.audioLevelWorker.onmessage = pollAudioLevel;
        this.audioLevelWorker.postMessage({ type: 'start', intervalMs: AUDIO_LEVEL_POLL_MS });
      } catch {
        // Fallback: setInterval for environments without Worker support (tests)
        this.audioLevelWorker = null;
        this.audioLevelTimerId = setInterval(pollAudioLevel, AUDIO_LEVEL_POLL_MS);
      }
    } catch (err) {
      // Audio analysis is non-critical — log but don't fail the recording
      SentryService.addBreadcrumb({
        category: 'recording.service',
        message: `Failed to start audio level analysis: ${err instanceof Error ? err.message : String(err)}`,
        level: 'warning',
      });
    }
  }

  private stopAudioLevelAnalysis(): void {
    if (this.audioLevelWorker) {
      this.audioLevelWorker.postMessage({ type: 'stop' });
      this.audioLevelWorker.terminate();
      this.audioLevelWorker = null;
    }
    if (this.audioLevelTimerId !== null) {
      clearInterval(this.audioLevelTimerId);
      this.audioLevelTimerId = null;
    }

    if (this.levelSourceNode) {
      try {
        this.levelSourceNode.disconnect();
      } catch {
        /* may already be disconnected */
      }
      this.levelSourceNode = null;
    }

    if (this.levelAnalyser) {
      try {
        this.levelAnalyser.disconnect();
      } catch {
        /* may already be disconnected */
      }
      this.levelAnalyser = null;
    }

    if (this.levelAudioContext) {
      if (this.levelAudioContext.state !== 'closed') {
        void this.levelAudioContext.close();
      }
      this.levelAudioContext = null;
    }

    this.levelTimeData = null;
  }

  // ── Web Lock (cross-tab exclusion) ──

  private async acquireWebLock(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.locks) {
      return true; // Feature not available — skip
    }

    try {
      return await new Promise<boolean>((resolve) => {
        navigator.locks.request(
          RECORDING_LOCK_NAME,
          { ifAvailable: true },
          (lock) => {
            if (!lock) {
              // Lock not available — another tab is recording
              SentryService.addBreadcrumb({
                category: 'recording.weblock',
                message: 'Web Lock unavailable — another tab holds it',
                level: 'warning',
              });
              resolve(false);
              return Promise.resolve();
            }

            // Lock acquired — hold it via a never-resolving promise
            // Release happens when we resolve the stored callback
            SentryService.addBreadcrumb({
              category: 'recording.weblock',
              message: 'Web Lock acquired',
              level: 'info',
            });
            return new Promise<void>((release) => {
              this.lockRelease = release;
              resolve(true);
            });
          },
        );
      });
    } catch (err) {
      // Web Lock API error — proceed without lock
      SentryService.addBreadcrumb({
        category: 'recording.weblock',
        message: `Web Lock API error: ${err instanceof Error ? err.message : String(err)}`,
        level: 'error',
      });
      return true;
    }
  }

  private releaseWebLock(): void {
    if (this.lockRelease) {
      SentryService.addBreadcrumb({
        category: 'recording.weblock',
        message: 'Web Lock released',
        level: 'info',
      });
      this.lockRelease();
      this.lockRelease = null;
    }
  }

  // ── Wake Lock (prevent device sleep) ──

  private async acquireWakeLock(): Promise<void> {
    if (typeof navigator === 'undefined' || !navigator.wakeLock) return;

    try {
      this.wakeLock = await navigator.wakeLock.request('screen');

      SentryService.addBreadcrumb({
        category: 'recording.wakelock',
        message: 'Wake lock acquired',
        level: 'info',
      });

      this.wakeLock.addEventListener('release', () => {
        // Browser auto-releases when page becomes hidden; this also fires
        // if iOS overrides the lock (low battery, system pressure).
        SentryService.addBreadcrumb({
          category: 'recording.wakelock',
          message: `Wake lock released (recording=${this.state.status})`,
          level: this.state.status === 'recording' ? 'warning' : 'info',
        });
        this.wakeLock = null;
      });
    } catch (error) {
      SentryService.addBreadcrumb({
        category: 'recording.wakelock',
        message: `Wake lock acquisition failed: ${error instanceof Error ? error.message : String(error)}`,
        level: 'warning',
      });
    }
  }

  private releaseWakeLock(): void {
    if (this.wakeLock) {
      SentryService.addBreadcrumb({
        category: 'recording.wakelock',
        message: 'Wake lock released (intentional)',
        level: 'info',
      });
      void this.wakeLock.release().catch(() => {});
      this.wakeLock = null;
    }
  }

  // ── Visibility Change ──

  private onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      // Flush buffered audio immediately when page goes hidden — tab might be killed
      if (this.recorder && this.recorder.state === 'recording') {
        try {
          this.recorder.requestData();
        } catch {
          // Ignore
        }
      }
      // Force immediate staging write, bypassing the throttle
      this.cancelPendingStagingWrite();
      void this.enqueuePipelineOp(() => this.writeStagingChunk());
    } else {
      // Resume mixer AudioContext if suspended (Chrome suspends in background tabs)
      if (this.mixerAudioContext?.state === 'suspended') {
        this.mixerAudioContext.resume().catch(() => {});
        SentryService.addBreadcrumb({
          category: 'recording.service',
          message: 'Mixer AudioContext resumed on visibility change',
          level: 'info',
        });
      }

      // Re-acquire Wake Lock when page becomes visible
      // (browsers release it when page is hidden)
      if (this.state.status === 'recording') {
        void this.acquireWakeLock();

        // Detect timer throttling: if gap between now and last tick is significantly
        // larger than expected, Chrome was throttling our timers. Cancel all intervals
        // and restart with fresh timing to prevent queued callbacks from avalanching.
        const timeSinceLastTick = Date.now() - this.lastRequestDataTimestamp;

        console.log(
          '[ThrottleGuard] Tab visible — timeSinceLastTick:',
          timeSinceLastTick,
          'ms, guardActive:',
          this.throttleGuard.isActive,
        );
        const throttleThresholdMs = this.requestDataIntervalMs * 15;

        if (this.lastRequestDataTimestamp > 0 && timeSinceLastTick > throttleThresholdMs) {
          console.warn(
            '[ThrottleGuard] Timer throttling detected! Gap:',
            Math.round(timeSinceLastTick / 1000),
            's — restarting timers',
          );
          SentryService.addBreadcrumb({
            category: 'recording.lifecycle',
            message: `Timer throttling detected on resume (gap=${Math.round(timeSinceLastTick / 1000)}s)`,
            level: 'warning',
            data: {
              expectedIntervalMs: this.requestDataIntervalMs,
              actualGapMs: timeSinceLastTick,
              throttleGuardActive: this.throttleGuard.isActive,
            },
          });

          // Stop all timers to cancel queued callbacks
          this.stopTimers();

          // Process one immediate requestData to flush buffered audio
          if (this.recorder?.state === 'recording') {
            try {
              this.recorder.requestData();
            } catch {
              /* ignore */
            }
          }

          // Restart timers fresh — no backlog
          this.startDurationTimer();
          this.startRequestDataTimer();
          this.startUploadBoundaryTimer();
          if (this.stream) {
            this.startAudioLevelAnalysis(this.stream);
          }
        }
      }
    }
  }

  // ── Tab Lifecycle (Chrome Page Lifecycle API) ──

  private onFreeze(): void {
    console.warn('[ThrottleGuard] Tab frozen by browser');
    SentryService.addBreadcrumb({
      category: 'recording.lifecycle',
      message: 'Tab frozen by browser',
      level: 'warning',
      data: {
        duration: this.state.duration,
        throttleGuardActive: this.throttleGuard.isActive,
        chunksInMemory: this.pendingChunks.length,
      },
    });
  }

  private onResume(): void {
    console.warn('[ThrottleGuard] Tab resumed from frozen state');
    SentryService.addBreadcrumb({
      category: 'recording.lifecycle',
      message: 'Tab resumed from frozen state',
      level: 'warning',
      data: {
        duration: this.state.duration,
        timeSinceLastTick: this.lastRequestDataTimestamp
          ? Date.now() - this.lastRequestDataTimestamp
          : null,
      },
    });
  }

  // ── State Helpers ──

  private setServiceStatus(status: RecordingServiceStatus): void {
    const prev = this.state.status;
    if (prev === status) return;
    this.updateState({ status });
    this.emit('statusChanged', { status, prev });
  }

  private updateState(partial: Partial<RecordingState>): void {
    this.setState({ ...this.state, ...partial });
  }

  private handleError(message: string, cause?: unknown): void {
    // Always release Web Lock on error to prevent cross-tab deadlock.
    this.releaseWebLock();
    this.releaseWakeLock();
    this.throttleGuard.deactivate();
    this.stopTimers();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('freeze', this.handleFreeze);
    document.removeEventListener('resume', this.handleResume);

    this.setServiceStatus('error');
    this.updateState({ error: message });
    this.emit('error', { message, cause });

    SentryService.captureException(new Error(message));
  }
}
